import React, { Context, createContext, useReducer, useEffect } from 'react';
import {
  BrandContextProps,
  BrandContext,
  BrandAction,
  BrandState,
  BrandActionType,
  BrandEffectType,
} from './Brand.types';

const BrandStore = createContext({} as BrandContext) as Context<BrandContext>;

const getInitialState = (): BrandState => {
  try {
    const savedState = JSON.parse(localStorage.getItem('selfreg.brandState')!);
    if (savedState && typeof savedState === 'object') {
      const state = savedState as BrandState;
      return {
        brandName: state.brandName,
        brand: state.brand,
        mcc: state.mcc,
        effect: null,
      };
    } else {
      throw new Error('unable to hydrate BrandStore from localStorage');
    }
  } catch (error) {
    return {
      brandName: null,
      brand: null,
      effect: null,
      mcc: null,
    };
  }
};

const initialState = getInitialState();

type Reducer = (state: BrandState, action: BrandAction) => BrandState;

const storeReducer: Reducer = (state, action) => {
  switch (action.type) {
    case BrandActionType.UPDATE_BRAND:
      const { brand, brandName, mcc } = action.payload!;
      return { ...state, brand, brandName, mcc };
    case BrandActionType.REMOVE_BRAND:
      return { ...state, brand: null };
    default:
      break;
  }

  return state;
};

const effectReducer: Reducer = (state, action) => {
  let newState = state;

  switch (action.type) {
    case BrandActionType.UPDATE_BRAND:
      newState = {
        ...state,
        effect: {
          type: BrandEffectType.UPDATE_BRAND,
          payload: state,
        },
      };
      break;
    default:
      break;
  }

  return newState;
};

const BrandProvider = (props: BrandContextProps) => {
  const [state, dispatch] = useReducer(
    (state, action) => effectReducer(storeReducer(state, action), action),
    props.initialState ?? initialState,
  );

  const { effect } = state;

  useEffect(() => {
    const effectType = effect && effect.type;

    switch (effectType) {
      case BrandEffectType.UPDATE_BRAND:
        localStorage.setItem(
          'selfreg.brandState',
          JSON.stringify(effect!.payload),
        );
        break;
      case BrandEffectType.REMOVE_BRAND:
        localStorage.removeItem('selfreg.brandState');
        break;
      default:
        break;
    }
  }, [effect]);

  const localContext: BrandContext = {
    state: state,
    dispatch: dispatch,
  };

  return (
    <BrandStore.Provider value={localContext}>
      {props.children}
    </BrandStore.Provider>
  );
};

export { BrandStore, BrandProvider };
